import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type {
  AcceptTermsPayload,
  AdminListUsersResponse,
  AdminUserDetail,
  AllowedRule,
  DeveloperResponse,
  MeProfileResponse,
  AdminListApisResponse,
  AdminCreateApiRequest,
  ApiRegistration,
} from '../types';

const adminBase = '/v1/dashboard/admin';

export function useDeveloperInfo() {
  return useQuery<DeveloperResponse>({
    queryKey: ['dashboard', 'me'],
    queryFn: async () => {
      const response = await apiClient.get<DeveloperResponse>('/v1/dashboard/me');
      return response.data;
    },
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useMeProfile() {
  return useQuery<MeProfileResponse>({
    queryKey: ['dashboard', 'me', 'profile'],
    queryFn: async () => {
      const response = await apiClient.get<MeProfileResponse>('/v1/dashboard/me/profile');
      return response.data;
    },
    retry: 1,
    staleTime: 0,
  });
}

export interface RegenerateAPIKeyResponse {
  apiKey: string;
  message: string;
}

export function useRegenerateAPIKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<RegenerateAPIKeyResponse> => {
      const response = await apiClient.post<RegenerateAPIKeyResponse>('/v1/dashboard/me/regenerate-key');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'me'] });
    },
  });
}

export function useAcceptTerms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AcceptTermsPayload) => {
      const response = await apiClient.post('/v1/dashboard/me/accept-terms', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'me', 'profile'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'me'] });
    },
  });
}

export function useDeclineAndClose() {
  return useMutation({
    mutationFn: async () => {
      await apiClient.post('/v1/dashboard/me/decline-and-close');
    },
  });
}

export function useAdminUsers() {
  return useQuery<AdminListUsersResponse>({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const response = await apiClient.get<AdminListUsersResponse>(`${adminBase}/users`);
      return response.data;
    },
    retry: 1,
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery<AdminUserDetail>({
    queryKey: ['admin', 'users', userId],
    queryFn: async () => {
      const response = await apiClient.get<AdminUserDetail>(`${adminBase}/users/${userId}`);
      return response.data;
    },
    enabled: !!userId,
    retry: 1,
  });
}

export function useAdminUpdatePermissions(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (allowed: AllowedRule[]) => {
      const response = await apiClient.put(`${adminBase}/users/${userId}/permissions`, { allowed });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
    },
  });
}

/** Mutation to update permissions for any user (pass userId in the first argument). */
export function useAdminUpdatePermissionsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, allowed }: { userId: string; allowed: AllowedRule[] }) => {
      const response = await apiClient.put(`${adminBase}/users/${userId}/permissions`, { allowed });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
}

export function useAdminUpdateEnabled(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiClient.put(`${adminBase}/users/${userId}/enabled`, { enabled });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
    },
  });
}

/** Mutation to update enabled for any user (pass userId in the first argument). */
export function useAdminUpdateEnabledMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, enabled }: { userId: string; enabled: boolean }) => {
      const response = await apiClient.put(`${adminBase}/users/${userId}/enabled`, { enabled });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
}

export function useAdminInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (email: string) => {
      const response = await apiClient.post(`${adminBase}/users/invite`, { email });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

/** Mutation to resend the Cognito invitation email for a user (RESEND action). */
export function useAdminResendInvite() {
  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiClient.post(`${adminBase}/users/${userId}/resend-invite`);
      return response.data as { message: string };
    },
  });
}

/** Mutation to regenerate the API key of an arbitrary user (admin only). */
export function useAdminRegenerateKey(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(`${adminBase}/users/${userId}/regenerate-key`);
      return response.data as { message: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
    },
  });
}

/** Mutation to reset a user's terms acceptance (admin only). Clears the Cognito
 *  custom:terms_accepted attribute, forces sign-out, and appends an audit row. */
export function useAdminResetTerms(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(`${adminBase}/users/${userId}/reset-terms`);
      return response.data as { message: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
    },
  });
}

/** Mutation to delete a user (removes API key and optionally Cognito account). */
export function useAdminDeleteUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiClient.delete(`${adminBase}/users/${userId}`);
      return response.data;
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
    },
  });
}

export function useAdminApis() {
  return useQuery<AdminListApisResponse>({
    queryKey: ['admin', 'apis'],
    queryFn: async () => {
      const response = await apiClient.get<AdminListApisResponse>(`${adminBase}/apis`);
      return response.data;
    },
    retry: 1,
  });
}

export function useAdminCreateApi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AdminCreateApiRequest) => {
      const response = await apiClient.post<ApiRegistration>(`${adminBase}/apis`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'apis'] });
    },
  });
}

export function useAdminUpdateApi(apiId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AdminCreateApiRequest) => {
      const response = await apiClient.put<ApiRegistration>(`${adminBase}/apis/${apiId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'apis'] });
    },
  });
}

export function useAdminDeleteApi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (apiId: string) => {
      const response = await apiClient.delete(`${adminBase}/apis/${apiId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'apis'] });
    },
  });
}
